import { UserApi } from "@/api/user.api";
import { $account } from "@/stores/account";
import { StytchClient } from "@/stores/stytch";
import { Button, Modal, PasswordInput, Text, TextInput } from "@mantine/core";
import { useStore } from "@nanostores/react";
import Cookies from "js-cookie";
import {
  createContext,
  FormEvent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";

const REDIRECT_URL = window.location.origin + "/auth/callback";
const SESSION_MINUTES = 1440;

// Custom headless login (2026-07-30). Replaces the pre-built StytchLogin
// widget, which framed every sign-in as a signup, did no returning-user
// detection, and never submitted a native <form> with autocomplete
// attributes — so Safari/Keychain never offered to save passwords.
// Flow: email step → server email-status check → "welcome back, enter
// your password" (existing) / "create your account" (new) / OAuth+link
// options (passwordless account). OAuth and magic links kept as fallbacks.

export type LoginModalOptions = {
  required?: boolean;
  message?: string;
  continueAsGuest?: boolean;
};

export type LoginModalContextType = {
  isOpen: boolean;
  open: (options?: LoginModalOptions) => void;
  close: () => void;
};

export const LoginModalContext = createContext<
  LoginModalContextType | undefined
>(undefined);

type Step =
  | "email"
  | "password"
  | "create"
  | "factors"
  | "link-sent"
  | "reset-sent";

const tealButton = {
  backgroundColor: "#4AA0A0",
  borderColor: "#4AA0A0",
  color: "white",
  fontFamily: "Afacad",
  height: "48px",
  fontSize: "18px",
  fontWeight: 500,
  borderRadius: "4px",
} as const;

const outlineButton = {
  backgroundColor: "white",
  borderColor: "#c8d2d2",
  color: "#333333",
  fontFamily: "Afacad",
  height: "48px",
  fontSize: "17px",
  fontWeight: 500,
  borderRadius: "4px",
} as const;

const linkStyle = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#2b807d",
  fontFamily: "Afacad",
  fontSize: 15,
  cursor: "pointer",
  textDecoration: "underline",
} as const;

const inputStyles = {
  input: {
    fontFamily: "Afacad",
    fontSize: 16,
    height: 46,
  },
  label: {
    fontFamily: "Afacad",
    fontSize: 15,
    marginBottom: 4,
  },
} as const;

export function LoginModalProvider({ children }: { children: ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(searchParams.has("login"));
  const [modalOptions, setModalOptions] = useState<LoginModalOptions>({});
  const account = useStore($account);

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  // True when the server email-status check failed — we don't know whether
  // this email has an account, so the password step also offers "create".
  const [statusUnknown, setStatusUnknown] = useState(false);
  // Recognized legacy (ASF-era) customer — shows the welcome-back badge on
  // the create step. Cosmetic only; recognition failure must never gate.
  const [legacyKnown, setLegacyKnown] = useState(false);
  // Legacy-recognized + unknown-status defaults to CREATE-FIRST (old ASF
  // passwords are the trap); this flips to the sign-in view on request.
  const [preferSignIn, setPreferSignIn] = useState(false);

  useEffect(() => {
    if (searchParams.has("login")) setIsOpen(true);
  }, [searchParams]);

  const resetFlow = () => {
    setStep("email");
    setPassword("");
    setBusy(false);
    setError("");
    setInfo("");
    setStatusUnknown(false);
  };

  const open = (options?: LoginModalOptions) => {
    setModalOptions(options || {});

    if (typeof window !== "undefined") {
      localStorage.setItem(
        "login_return_url",
        window.location.pathname + window.location.search
      );
    }

    resetFlow();
    setIsOpen(true);
  };

  const close = () => {
    // Don't allow closing if modal is required
    if (modalOptions.required) return;

    setIsOpen(false);
    setModalOptions({});
    resetFlow();
    if (searchParams.has("login")) {
      searchParams.delete("login");
      setSearchParams(searchParams);
    }
  };

  // After any successful in-page authentication: persist the session the
  // same way AuthCallback does, make sure /session/update has run (that's
  // where auto-claim attaches this email's unowned orders), then do a full
  // navigation — which is also the moment Safari offers to save the
  // password to Keychain.
  const onAuthSuccess = async () => {
    const user = StytchClient.user.getSync();
    const session = StytchClient.session.getSync();

    if (user && session) {
      Cookies.set("stytch_user_id", session.user_id, { expires: 1 });
      Cookies.set("stytch_user_email", user.emails?.[0]?.email || "", {
        expires: 1,
      });
      await UserApi.updateSession({ session, user }).catch(() => null);
    }

    const returnUrl =
      localStorage.getItem("login_return_url") ||
      window.location.pathname + window.location.search;
    localStorage.removeItem("login_return_url");
    window.location.href = returnUrl;
  };

  const handleEmailContinue = async (e: FormEvent) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!clean || !clean.includes("@")) {
      setError("Please enter your email address.");
      return;
    }
    setEmail(clean);
    setBusy(true);
    setError("");
    try {
      const { data } = await UserApi.emailStatus(clean);
      setLegacyKnown(!!data.legacy);
      setPreferSignIn(false);
      if (data.ok && data.exists && data.hasPassword) {
        setStep("password");
      } else if (data.ok && data.exists && !data.hasPassword) {
        setStep("factors");
      } else if (data.ok && !data.exists) {
        setStep("create");
      } else {
        // Status unknown — offer password sign-in with a create fallback.
        setStatusUnknown(true);
        setStep("password");
      }
    } catch {
      setStatusUnknown(true);
      setStep("password");
    } finally {
      setBusy(false);
    }
  };

  const handlePasswordLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError("Please enter your password.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await StytchClient.passwords.authenticate({
        email,
        password,
        session_duration_minutes: SESSION_MINUTES,
      });
      await onAuthSuccess();
    } catch (err: any) {
      const type = err?.error_type || "";
      if (type === "unauthorized_credentials") {
        setError(
          "That password doesn't look right. Try again, or reset it below."
        );
      } else if (type === "reset_password") {
        await sendPasswordReset(
          "For security, you need to set a new password. We've emailed you a link."
        );
      } else if (type.includes("lock")) {
        setError("Too many attempts. Please wait a few minutes and try again.");
      } else if (type === "user_not_found" || type === "email_not_found") {
        setError(
          "We couldn't find an account for that email. Create one below."
        );
        setStatusUnknown(true);
      } else {
        setError(err?.error_message || "Sign-in failed. Please try again.");
      }
      setBusy(false);
    }
  };

  const handleCreateAccount = async (e: FormEvent) => {
    e.preventDefault();
    if (!password) {
      setError("Please choose a password.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const strength = await StytchClient.passwords.strengthCheck({
        email,
        password,
      });
      if (!strength.valid_password) {
        const feedback =
          strength.feedback?.warning ||
          strength.feedback?.suggestions?.[0] ||
          "Please choose a longer, less common password.";
        setError(feedback);
        setBusy(false);
        return;
      }
      await StytchClient.passwords.create({
        email,
        password,
        session_duration_minutes: SESSION_MINUTES,
      });
      await onAuthSuccess();
    } catch (err: any) {
      const type = err?.error_type || "";
      if (type === "duplicate_email") {
        setInfo("Looks like you already have an account — enter your password.");
        setError("");
        setPreferSignIn(true); // must land on the password form, not create-first
        setStep("password");
      } else if (type === "weak_password" || type === "breached_password") {
        setError(
          "That password is too easy to guess or has appeared in a data breach — please choose another."
        );
      } else {
        setError(err?.error_message || "Sign-up failed. Please try again.");
      }
      setBusy(false);
    }
  };

  const sendMagicLink = async () => {
    setBusy(true);
    setError("");
    try {
      await StytchClient.magicLinks.email.loginOrCreate(email, {
        login_magic_link_url: REDIRECT_URL,
        signup_magic_link_url: REDIRECT_URL,
        login_expiration_minutes: SESSION_MINUTES,
        signup_expiration_minutes: SESSION_MINUTES,
      });
      setStep("link-sent");
    } catch (err: any) {
      setError(err?.error_message || "Couldn't send the link. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const sendPasswordReset = async (message?: string) => {
    setBusy(true);
    setError("");
    try {
      await StytchClient.passwords.resetByEmailStart({
        email,
        reset_password_redirect_url: REDIRECT_URL,
        login_redirect_url: REDIRECT_URL,
        reset_password_expiration_minutes: SESSION_MINUTES,
      });
      setInfo(message || "");
      setStep("reset-sent");
    } catch (err: any) {
      setError(err?.error_message || "Couldn't send the email. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const startOAuth = (provider: "google" | "apple" | "facebook") => {
    const options = {
      login_redirect_url: REDIRECT_URL,
      signup_redirect_url: REDIRECT_URL,
    };
    if (provider === "facebook") {
      StytchClient.oauth.facebook
        .start({ ...options, custom_scopes: ["public_profile", "email"] })
        .catch(() => setError("Couldn't start Facebook sign-in. Try again."));
    } else if (provider === "apple") {
      StytchClient.oauth.apple
        .start(options)
        .catch(() => setError("Couldn't start Apple sign-in. Try again."));
    } else {
      StytchClient.oauth.google
        .start(options)
        .catch(() => setError("Couldn't start Google sign-in. Try again."));
    }
  };

  const backToEmail = () => {
    setStep("email");
    setPassword("");
    setError("");
    setInfo("");
    setStatusUnknown(false);
  };

  const oauthButtons = (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <Button fullWidth size="lg" style={outlineButton} onClick={() => startOAuth("apple")}>
        Continue with Apple
      </Button>
      <Button fullWidth size="lg" style={outlineButton} onClick={() => startOAuth("google")}>
        Continue with Google
      </Button>
      <Button fullWidth size="lg" style={outlineButton} onClick={() => startOAuth("facebook")}>
        Continue with Facebook
      </Button>
    </div>
  );

  const divider = (
    <div className="my-4 flex h-[21px] items-center justify-center gap-[6px]">
      <div className="h-[1px] w-full bg-[#5c727d]"></div>
      <div className="text-md font-normal text-[#5c727d]">or</div>
      <div className="h-[1px] w-full bg-[#5c727d]"></div>
    </div>
  );

  const errorText = error && (
    <Text size="sm" c="red" mt={8} style={{ fontFamily: "Afacad" }}>
      {error}
    </Text>
  );

  const infoText = info && (
    <Text size="sm" c="dimmed" mb={8} style={{ fontFamily: "Afacad" }}>
      {info}
    </Text>
  );

  const emailLine = (
    <div style={{ marginBottom: 8 }}>
      <Text size="sm" style={{ fontFamily: "Afacad", color: "#333" }}>
        {email}{" "}
        <button type="button" style={linkStyle} onClick={backToEmail}>
          change
        </button>
      </Text>
    </div>
  );

  return (
    <LoginModalContext.Provider value={{ isOpen, open: open, close }}>
      <Modal
        opened={isOpen}
        onClose={close}
        withCloseButton={!modalOptions.continueAsGuest}
        closeOnClickOutside={!modalOptions.required}
        closeOnEscape={!modalOptions.required}
        size="lg"
        zIndex={9999}
      >
        {modalOptions.message && (
          <Text
            size="sm"
            c="dimmed"
            ta="center"
            px="md"
            style={{ lineHeight: 1.5 }}
          >
            {modalOptions.message}
          </Text>
        )}

        <div
          style={{
            width: "min(600px, 100%)",
            margin: "0 auto",
            padding: "8px 32px 0",
            fontFamily: "Afacad",
          }}
        >
          <img
            src="/favicon.ico"
            alt=""
            style={{ width: 48, height: 48, display: "block", margin: "0 auto 8px" }}
          />

          {step === "email" && (
            <>
              <Text ta="center" fw={600} fz={22} mb={12} style={{ fontFamily: "Afacad" }}>
                Sign in or create your account
              </Text>
              <form onSubmit={handleEmailContinue} method="post" action="#">
                <TextInput
                  type="email"
                  name="email"
                  label="Email address"
                  autoComplete="username"
                  value={email}
                  onChange={e => setEmail(e.currentTarget.value)}
                  styles={inputStyles}
                  required
                />
                {errorText}
                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  mt={14}
                  loading={busy}
                  style={tealButton}
                >
                  Continue
                </Button>
              </form>
              {divider}
              {oauthButtons}
            </>
          )}

          {step === "password" && statusUnknown && legacyKnown && !preferSignIn && (
            <>
              <Text ta="center" fw={600} fz={22} mb={4} style={{ fontFamily: "Afacad" }}>
                Welcome back!
              </Text>
              {emailLine}
              {infoText}
              <div
                style={{
                  backgroundColor: "#dbf1f1",
                  border: "1px solid #b7e0e0",
                  borderRadius: 8,
                  padding: "14px 16px",
                  marginTop: 6,
                }}
              >
                <Text
                  size="sm"
                  ta="center"
                  style={{ fontFamily: "Afacad", lineHeight: 1.55, fontSize: 16 }}
                >
                  <strong>Good to see you again!</strong> Since you&apos;ve
                  been gone we&apos;ve rebuilt from the ground up &mdash; this
                  is a brand-new ordering platform, completely separate from
                  our previous site, with its own accounts. Keep using this
                  same email &mdash; it&apos;s how we recognize you &mdash;
                  and simply set a new password. Your contact info and order
                  history comes with you.
                </Text>
                <Button
                  fullWidth
                  size="lg"
                  mt={12}
                  style={tealButton}
                  onClick={() => {
                    setPassword("");
                    setError("");
                    setStep("create");
                  }}
                >
                  Create my new account
                </Button>
                <Text
                  size="xs"
                  ta="center"
                  mt={10}
                  c="dimmed"
                  style={{ fontFamily: "Afacad" }}
                >
                  Your original account &amp; order history stay available at{" "}
                  <a
                    href="https://legacy.tribecaprintworks.com"
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#2b807d", fontWeight: 700 }}
                  >
                    legacy.tribecaprintworks.com
                  </a>
                </Text>
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  marginTop: 14,
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  style={linkStyle}
                  onClick={() => {
                    setError("");
                    setPreferSignIn(true);
                  }}
                >
                  Already created your new studio account? Sign in
                </button>
              </div>
            </>
          )}

          {step === "password" && !(statusUnknown && legacyKnown && !preferSignIn) && (
            <>
              <Text ta="center" fw={600} fz={22} mb={4} style={{ fontFamily: "Afacad" }}>
                {statusUnknown ? "Sign in or create your account" : "Welcome back"}
              </Text>
              {emailLine}
              {infoText}
              {statusUnknown && (
                <Text
                  size="sm"
                  fw={700}
                  mt={6}
                  mb={-4}
                  style={{ fontFamily: "Afacad" }}
                >
                  {legacyKnown
                    ? "Already created your new login? Enter your new password here."
                    : "Already have a studio account?"}
                </Text>
              )}
              <form onSubmit={handlePasswordLogin} method="post" action="#">
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  value={email}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                />
                <PasswordInput
                  name="password"
                  label="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={e => setPassword(e.currentTarget.value)}
                  styles={inputStyles}
                  autoFocus
                  required
                />
                {errorText}
                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  mt={14}
                  loading={busy}
                  style={tealButton}
                >
                  Sign in
                </Button>
              </form>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  marginTop: 12,
                  alignItems: "center",
                }}
              >
                <button type="button" style={linkStyle} onClick={() => sendPasswordReset()}>
                  Forgot password?
                </button>
                <button type="button" style={linkStyle} onClick={sendMagicLink}>
                  Email me a sign-in link instead
                </button>
              </div>
              {statusUnknown && (
                <>
                  <Text
                    ta="center"
                    size="sm"
                    c="dimmed"
                    my={10}
                    style={{ fontFamily: "Afacad" }}
                  >
                    &mdash;&ensp;or&ensp;&mdash;
                  </Text>
                  <div
                    style={{
                      backgroundColor: "#dbf1f1",
                      border: "1px solid #b7e0e0",
                      borderRadius: 8,
                      padding: "12px 16px",
                    }}
                  >
                    {legacyKnown ? (
                      <Text
                        size="sm"
                        ta="center"
                        style={{ fontFamily: "Afacad", lineHeight: 1.5 }}
                      >
                        <strong>Welcome back &mdash; you&apos;re one step away.</strong>{" "}
                        Your old password belongs to our previous site, not
                        this one. Create your new account and your info comes
                        with you. Your original account and order history stay
                        available at{" "}
                        <a
                          href="https://legacy.tribecaprintworks.com"
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#2b807d", fontWeight: 700 }}
                        >
                          legacy.tribecaprintworks.com
                        </a>
                        .
                      </Text>
                    ) : (
                      <Text
                        size="sm"
                        ta="center"
                        style={{ fontFamily: "Afacad", lineHeight: 1.5 }}
                      >
                        <strong>New to the studio?</strong> Our new studio uses
                        a fresh account &mdash; it takes seconds to create.
                        Ordered on our previous site? Your original account and
                        history stay available at{" "}
                        <a
                          href="https://legacy.tribecaprintworks.com"
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: "#2b807d", fontWeight: 700 }}
                        >
                          legacy.tribecaprintworks.com
                        </a>
                        .
                      </Text>
                    )}
                    <Button
                      fullWidth
                      size="md"
                      mt={10}
                      variant="outline"
                      style={{
                        borderColor: "#2b807d",
                        color: "#2b807d",
                        fontFamily: "Afacad",
                      }}
                      onClick={() => {
                        setPassword("");
                        setError("");
                        setStep("create");
                      }}
                    >
                      Create an account
                    </Button>
                  </div>
                </>
              )}
            </>
          )}

          {step === "create" && (
            <>
              <Text ta="center" fw={600} fz={22} mb={4} style={{ fontFamily: "Afacad" }}>
                Create your account
              </Text>
              {emailLine}
              {legacyKnown && (
                <div
                  style={{
                    backgroundColor: "#dbf1f1",
                    border: "1px solid #b7e0e0",
                    borderRadius: 8,
                    padding: "10px 14px",
                    margin: "4px 0 10px",
                  }}
                >
                  <Text
                    size="sm"
                    ta="center"
                    style={{ fontFamily: "Afacad", lineHeight: 1.5, fontSize: 16 }}
                  >
                    <strong>Good to see you again!</strong> Since you&apos;ve
                    been gone we&apos;ve rebuilt from the ground up &mdash;
                    this is a brand-new ordering platform, completely separate
                    from our previous site, with its own accounts. Keep using
                    this same email &mdash; it&apos;s how we recognize you
                    &mdash; and simply set a new password below. Your contact
                    info and order history comes with you.
                  </Text>
                </div>
              )}
              <form onSubmit={handleCreateAccount} method="post" action="#">
                <input
                  type="email"
                  name="email"
                  autoComplete="username"
                  value={email}
                  readOnly
                  tabIndex={-1}
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    width: 1,
                    height: 1,
                    opacity: 0,
                    pointerEvents: "none",
                  }}
                />
                <PasswordInput
                  name="new-password"
                  label="Choose a password"
                  autoComplete="new-password"
                  value={password}
                  onChange={e => setPassword(e.currentTarget.value)}
                  styles={inputStyles}
                  autoFocus
                  required
                />
                {errorText}
                <Button
                  type="submit"
                  fullWidth
                  size="lg"
                  mt={14}
                  loading={busy}
                  style={tealButton}
                >
                  Create account
                </Button>
              </form>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  marginTop: 12,
                  alignItems: "center",
                }}
              >
                <button type="button" style={linkStyle} onClick={sendMagicLink}>
                  Email me a sign-in link instead
                </button>
              </div>
              {divider}
              {oauthButtons}
            </>
          )}

          {step === "factors" && (
            <>
              <Text ta="center" fw={600} fz={22} mb={4} style={{ fontFamily: "Afacad" }}>
                Welcome back
              </Text>
              {emailLine}
              <Text size="sm" c="dimmed" mb={12} style={{ fontFamily: "Afacad" }}>
                You've signed in here before with Apple, Google, Facebook, or an
                email link — pick one below. You can also set a password for
                next time.
              </Text>
              {errorText}
              {oauthButtons}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  marginTop: 12,
                  alignItems: "center",
                }}
              >
                <button type="button" style={linkStyle} onClick={sendMagicLink}>
                  Email me a sign-in link
                </button>
                <button
                  type="button"
                  style={linkStyle}
                  onClick={() =>
                    sendPasswordReset(
                      "We've emailed you a link to set your password."
                    )
                  }
                >
                  Set a password
                </button>
              </div>
            </>
          )}

          {(step === "link-sent" || step === "reset-sent") && (
            <>
              <Text ta="center" fw={600} fz={22} mb={8} style={{ fontFamily: "Afacad" }}>
                Check your email
              </Text>
              {infoText}
              <Text size="sm" style={{ fontFamily: "Afacad", lineHeight: 1.55 }}>
                We sent {step === "link-sent" ? "a sign-in link" : "a password link"} to{" "}
                <strong>{email}</strong>. It may take a minute — check spam too.
              </Text>
              {errorText}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  marginTop: 12,
                  alignItems: "center",
                }}
              >
                <button
                  type="button"
                  style={linkStyle}
                  onClick={() =>
                    step === "link-sent" ? sendMagicLink() : sendPasswordReset()
                  }
                >
                  Resend email
                </button>
                <button type="button" style={linkStyle} onClick={backToEmail}>
                  Use a different email
                </button>
              </div>
            </>
          )}
        </div>

        {!legacyKnown && !(step === "password" && statusUnknown) && (
        <div
          style={{
            width: "min(600px, 100%)",
            margin: "0 auto",
            padding: "16px 32px 16px",
          }}
        >
          <div
            style={{
              backgroundColor: "#dbf1f1",
              border: "1px solid #b7e0e0",
              borderRadius: 10,
              padding: "14px 18px",
              fontFamily: "Afacad",
              fontSize: 16,
              lineHeight: 1.55,
              color: "#333333",
            }}
          >
            <p style={{ margin: 0 }}>
              <strong style={{ color: "#1a1a1a" }}>
                First time signing in here?
              </strong>{" "}
              Our new studio uses a fresh account &mdash; it takes seconds to
              create!
            </p>
            <p style={{ margin: "10px 0 0" }}>
              Existing customers don&rsquo;t worry: your account, uploads, and
              order history are completely safe and fully usable on our
              original ordering site, now at:{" "}
              <a
                href="https://legacy.tribecaprintworks.com"
                target="_blank"
                rel="noreferrer"
                style={{ color: "#2b807d", fontWeight: 700, fontSize: 18 }}
              >
                legacy.tribecaprintworks.com
              </a>
            </p>
          </div>
        </div>
        )}
        {(modalOptions.continueAsGuest || !modalOptions.required) &&
          !account.isLoggedIn && (
          <div
            style={{
              position: "sticky",
              bottom: 0,
              width: "100%",
              background: "#ffffff",
              boxShadow: "0 -6px 14px rgba(0, 0, 0, 0.06)",
              zIndex: 3,
              paddingTop: 8,
            }}
          >
            <div
              style={{
                width: "min(600px, 100%)",
                margin: "0 auto",
                padding: "12px 32px 20px",
              }}
            >
              <div className="mb-4 flex h-[21px] items-center justify-center gap-[6px]">
                <div className="h-[1px] w-full bg-[#5c727d]"></div>
                <div className="text-md font-normal text-[#5c727d]">or</div>
                <div className="h-[1px] w-full bg-[#5c727d]"></div>
              </div>
              <Button
                fullWidth
                size="lg"
                variant="default"
                style={tealButton}
                onClick={() => {
                  close();
                }}
              >
                Continue as Guest
              </Button>
            </div>
          </div>
        )}
      </Modal>
      {children}
    </LoginModalContext.Provider>
  );
}
